import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
    Box, Grid, TextField, Typography, Button, Divider, MenuItem, Select,
    FormControl, InputLabel, Paper, CircularProgress, Snackbar, InputAdornment,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    IconButton, Tooltip, Chip, Pagination, Stack, Dialog, DialogTitle,
    DialogContent, DialogActions
} from '@mui/material';
import MuiAlert from '@mui/material/Alert';
import PrintIcon from '@mui/icons-material/Print';
import SearchIcon from '@mui/icons-material/Search';
import SaveIcon from '@mui/icons-material/Save';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { format } from 'date-fns';
import { PDFDocument } from 'pdf-lib';
import html2canvas from 'html2canvas';
import api, { baseURL } from '../../services/api';
import * as qs from 'qs';
import { maskCNPJ, maskCPF2 } from '../../utils/mask';
import Assinatura from '../../assets/ass.png';
import { useAuth } from '../../contexts/auth.context';

const createEmptyForm = (emitterDoc = '') => ({
    payerName: '',
    payerDoc: '',
    referente: '',
    value: '',
    emitterDoc,
    emitterName: '',
    emitterPhone: '',
    emitterCity: '',
    paymentDate: format(new Date(), 'yyyy-MM-dd'),
    observations: '',
});

const Alert = React.forwardRef(function Alert(props, ref) {
    return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

function formatCurrency(val) {
    const num = parseCurrencyNumber(val);
    if (Number.isNaN(num)) return '';
    return num.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function parseCurrencyNumber(value) {
    if (value === null || value === undefined || value === '') return NaN;

    if (typeof value === 'number') return value;

    const cleaned = value.toString().replace(/[^\d,.-]/g, '');
    if (!cleaned) return NaN;

    const normalized = cleaned.includes(',')
        ? cleaned.replace(/\./g, '').replace(',', '.')
        : cleaned;

    return parseFloat(normalized);
}

function digitsOnly(value) {
    return String(value || '').replace(/\D/g, '');
}

function formatCpfCnpj(value) {
    const digits = digitsOnly(value);
    if (digits.length <= 11) return maskCPF2(digits);
    return maskCNPJ(digits);
}

function maskCurrencyPtBr(value) {
    const digits = digitsOnly(value);
    if (!digits) return '';
    const amount = Number(digits) / 100;
    return amount.toLocaleString('pt-BR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });
}

function maskPhoneBr(value) {
    const digits = digitsOnly(value).slice(0, 11);
    if (!digits) return '';

    if (digits.length <= 2) {
        return `(${digits}`;
    }

    if (digits.length <= 6) {
        return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
    }

    if (digits.length <= 10) {
        return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`;
    }

    return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
}

function numberToPortugueseWords(n) {
    const units = ['zero', 'um', 'dois', 'três', 'quatro', 'cinco', 'seis', 'sete', 'oito', 'nove'];
    const teens = ['dez', 'onze', 'doze', 'treze', 'quatorze', 'quinze', 'dezesseis', 'dezessete', 'dezoito', 'dezenove'];
    const tens = ['', '', 'vinte', 'trinta', 'quarenta', 'cinquenta', 'sessenta', 'setenta', 'oitenta', 'noventa'];
    const hundreds = ['', 'cento', 'duzentos', 'trezentos', 'quatrocentos', 'quinhentos', 'seiscentos', 'setecentos', 'oitocentos', 'novecentos'];
    const scales = [
        { singular: '', plural: '' },
        { singular: 'mil', plural: 'mil' },
        { singular: 'milhão', plural: 'milhões' },
        { singular: 'bilhão', plural: 'bilhões' },
        { singular: 'trilhão', plural: 'trilhões' },
    ];

    const groupToWords = (num) => {
        if (num === 0) return '';
        if (num === 100) return 'cem';

        const h = Math.floor(num / 100);
        const d = Math.floor((num % 100) / 10);
        const u = num % 10;
        const parts = [];

        if (h > 0) parts.push(hundreds[h]);
        if (d === 1) {
            parts.push(teens[u]);
        } else {
            if (d > 1) parts.push(tens[d]);
            if (u > 0) parts.push(units[u]);
        }

        return parts.join(' e ');
    };

    if (n === 0) return 'zero';

    const groups = [];
    let value = n;
    while (value > 0) {
        groups.push(value % 1000);
        value = Math.floor(value / 1000);
    }

    const parts = [];
    for (let i = groups.length - 1; i >= 0; i -= 1) {
        const groupValue = groups[i];
        if (groupValue === 0) continue;

        const scale = scales[i] || scales[scales.length - 1];
        const words = groupToWords(groupValue);

        if (i === 1) {
            parts.push(groupValue === 1 ? 'mil' : `${words} mil`);
        } else if (i > 1) {
            parts.push(`${words} ${groupValue === 1 ? scale.singular : scale.plural}`);
        } else {
            parts.push(words);
        }
    }

    if (parts.length === 1) return parts[0];
    return `${parts.slice(0, -1).join(', ')} e ${parts[parts.length - 1]}`;
}

function currencyToWords(value) {
    const num = parseCurrencyNumber(value);
    if (Number.isNaN(num)) return '';

    const absolute = Math.abs(num);
    let inteiro = Math.floor(absolute);
    let centavos = Math.round((absolute - inteiro) * 100);

    if (centavos === 100) {
        inteiro += 1;
        centavos = 0;
    }

    const inteiroWords = numberToPortugueseWords(inteiro);
    const inteiroLabel = inteiro === 1 ? 'real' : 'reais';
    const centavosWords = centavos > 0 ? numberToPortugueseWords(centavos) : '';
    const centavosLabel = centavos === 1 ? 'centavo' : 'centavos';

    let result = `${inteiroWords} ${inteiroLabel}`;
    if (centavos > 0) {
        result += ` e ${centavosWords} ${centavosLabel}`;
    }

    if (num < 0) {
        result = `menos ${result}`;
    }

    return result;
}

export default function Recibos() {
    const [form, setForm] = useState(createEmptyForm());
    const [searchingEmitter, setSearchingEmitter] = useState(false);
    const [searchingPayer, setSearchingPayer] = useState(false);
    const [saving, setSaving] = useState(false);
    const { basePath } = useAuth();
    const [alert, setAlert] = useState({ open: false, type: 'success', msg: '' });
    const receiptRef = useRef();
    const defaultEmitterDoc = useRef('');
    const [configLogoUrl, setConfigLogoUrl] = useState('');

    // Lista de recibos salvos
    const [receipts, setReceipts] = useState([]);
    const [loadingList, setLoadingList] = useState(false);
    const [pages, setPages] = useState({ pageCount: 1 });
    const currentPage = useRef(1);
    const [searchQuery, setSearchQuery] = useState('');
    const [deleteConfirm, setDeleteConfirm] = useState({ open: false, id: null });
    const [emitterSearchModalOpen, setEmitterSearchModalOpen] = useState(false);
    const [emitterSearchName, setEmitterSearchName] = useState('');
    const [emitterSearchResults, setEmitterSearchResults] = useState([]);
    const [emitterSearchLoading, setEmitterSearchLoading] = useState(false);
    const [emitterSearchAttempted, setEmitterSearchAttempted] = useState(false);
    const [payerSearchModalOpen, setPayerSearchModalOpen] = useState(false);
    const [payerSearchName, setPayerSearchName] = useState('');
    const [payerSearchResults, setPayerSearchResults] = useState([]);
    const [payerSearchLoading, setPayerSearchLoading] = useState(false);
    const [payerSearchAttempted, setPayerSearchAttempted] = useState(false);
    const [lockCriticalFields, setLockCriticalFields] = useState(false);
    const [pdfPreviewOpen, setPdfPreviewOpen] = useState(false);
    const [pdfPreviewUrl, setPdfPreviewUrl] = useState('');
    const [generatingPdf, setGeneratingPdf] = useState(false);

    // Metadados exibidos no recibo (ID e data de criação)
    const receiptNumber = useRef('');
    const receiptCreatedAt = useRef('');

    const handleField = (field) => (e) => {
        setForm(prev => ({ ...prev, [field]: e.target.value }));
    };

    const handleValueChange = (e) => {
        setForm(prev => ({ ...prev, value: maskCurrencyPtBr(e.target.value) }));
    };

    const handleEmitterPhoneChange = (e) => {
        setForm(prev => ({ ...prev, emitterPhone: maskPhoneBr(e.target.value) }));
    };

    const resolveAbsoluteUrl = (url) => {
        if (!url) return '';
        if (/^https?:\/\//i.test(url)) return url;
        const root = baseURL || window.location.origin;
        return `${root}${url.startsWith('/') ? '' : '/'}${url}`;
    };

    const getMediaUrl = (mediaField) => (
        mediaField?.data?.attributes?.url || mediaField?.url || ''
    );

    const extractConfigAttributes = (responseData) => (
        responseData?.data?.attributes
        || responseData?.attributes
        || {}
    );

    const getSubscriberCompanyAttributes = (subscriber = {}) => {
        const companyData = subscriber?.company?.data;
        if (Array.isArray(companyData)) {
            return companyData[0]?.attributes || {};
        }
        return companyData?.attributes || {};
    };

    const getSubscriberPhone = (subscriber = {}) => (
        subscriber?.whatsapp || subscriber?.phone || ''
    );

    const getSubscriberCity = (subscriber = {}) => {
        const companyAttributes = getSubscriberCompanyAttributes(subscriber);
        return companyAttributes?.city || subscriber?.city || '';
    };

    const getCompanyByDocument = async (doc) => {
        const document = digitsOnly(doc);
        if (!document) return null;

        const query = qs.stringify({
            filters: {
                $or: [
                    { document: { $eq: doc } },
                    { document: { $eq: document } },
                ],
            },
            populate: '*',
            encodeValuesOnly: true,
        });
        const response = await api.get('/companies?' + query);
        const data = response.data.data;
        if (data && data.length > 0) {
            return data[0].attributes;
        }
        return null;
    };

    const getSubscriberByDocument = async (doc) => {
        const document = digitsOnly(doc);
        if (!document) return null;

        const query = qs.stringify({
            filters: {
                $or: [
                    { document: { $eq: doc } },
                    { document: { $eq: document } },
                ],
            },
            sort: ['name:asc'],
            pagination: { page: 1, pageSize: 1 },
            populate: {
                company: {
                    fields: ['city'],
                },
            },
            encodeValuesOnly: true,
        });
        const response = await api.get('/subscribers?' + query);
        const data = response.data.data;
        if (data && data.length > 0) {
            return data[0].attributes;
        }
        return null;
    };

    const getPayerByDocument = async (doc) => {
        const company = await getCompanyByDocument(doc);
        if (company) {
            return {
                source: 'company',
                name: company.social_reason || company.trade || '',
                document: company.document || doc,
                raw: company,
            };
        }

        const subscriber = await getSubscriberByDocument(doc);
        if (subscriber) {
            return {
                source: 'subscriber',
                name: subscriber.name || '',
                document: subscriber.document || doc,
                phone: getSubscriberPhone(subscriber),
                city: getSubscriberCity(subscriber),
                raw: subscriber,
            };
        }

        return null;
    };

    const getEmitterByDocument = async (doc) => {
        const company = await getCompanyByDocument(doc);
        if (company) {
            return {
                source: 'company',
                name: company.social_reason || company.trade || '',
                document: company.document || doc,
                phone: company.phone || '',
                city: company.city || '',
                raw: company,
            };
        }

        const subscriber = await getSubscriberByDocument(doc);
        if (subscriber) {
            return {
                source: 'subscriber',
                name: subscriber.name || '',
                document: subscriber.document || doc,
                phone: getSubscriberPhone(subscriber),
                city: getSubscriberCity(subscriber),
                raw: subscriber,
            };
        }

        return null;
    };

    const searchEmitterByDocument = async (doc, { silent = false } = {}) => {
        setSearchingEmitter(true);
        try {
            const emitter = await getEmitterByDocument(doc);
            if (emitter) {
                setForm(prev => ({
                    ...prev,
                    emitterDoc: formatCpfCnpj(emitter.document || doc),
                    emitterName: emitter.name || '',
                    emitterPhone: maskPhoneBr(emitter.phone || ''),
                    emitterCity: emitter.city || '',
                }));
                if (!silent) {
                    setAlert({ open: true, type: 'success', msg: 'Dados do emissor preenchidos automaticamente.' });
                }
            } else if (!silent) {
                setAlert({ open: true, type: 'warning', msg: 'Emissor não encontrado para o documento informado.' });
            }
        } catch (err) {
            if (!silent) {
                setAlert({ open: true, type: 'error', msg: 'Erro ao buscar emissor pelo documento.' });
            }
        } finally {
            setSearchingEmitter(false);
        }
    };

    useEffect(() => {
        const loadDefaultConfig = async () => {
            try {
                const response = await api.get('/configs/1?populate=*');

                const attributes = extractConfigAttributes(response.data);
                const documentFromConfig = maskCNPJ(
                    attributes.document || ''
                );
                const logoFromConfig = resolveAbsoluteUrl(
                    getMediaUrl(attributes.logo)
                );

                if (documentFromConfig) {
                    defaultEmitterDoc.current = documentFromConfig;
                    setForm(prev => ({ ...prev, emitterDoc: documentFromConfig }));
                    // Usa o documento da config para buscar dados atualizados em companies.
                    searchEmitterByDocument(documentFromConfig, { silent: true });
                }

                if (logoFromConfig) {
                    setConfigLogoUrl(logoFromConfig);
                }
            } catch (err) {
                console.error('Erro ao carregar configuração padrão de recibos:', err);
            }
        };

        loadDefaultConfig();
        // Executa apenas no carregamento da tela.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleEmitterCNPJChange = (e) => {
        const formatted = formatCpfCnpj(e.target.value);
        setForm(prev => ({ ...prev, emitterDoc: formatted }));
    };

    const openEmitterSearchModal = () => {
        setEmitterSearchModalOpen(true);
        setEmitterSearchAttempted(false);
        setEmitterSearchResults([]);
        setEmitterSearchName('');
    };

    const handleSearchEmitterByName = async () => {
        const searchTerm = emitterSearchName.trim();
        const normalizedSearchTerm = digitsOnly(searchTerm);

        if (searchTerm.length < 2) {
            setAlert({ open: true, type: 'warning', msg: 'Digite ao menos 2 caracteres para pesquisar emissor.' });
            return;
        }

        setEmitterSearchLoading(true);
        setEmitterSearchAttempted(true);
        try {
            const companyQuery = qs.stringify({
                filters: {
                    $or: [
                        { social_reason: { $containsi: searchTerm } },
                        { trade: { $containsi: searchTerm } },
                        { document: { $containsi: normalizedSearchTerm || searchTerm } },
                    ],
                },
                sort: ['social_reason:asc'],
                pagination: { page: 1, pageSize: 150 },
                encodeValuesOnly: true,
            });

            const subscriberQuery = qs.stringify({
                filters: {
                    $or: [
                        { name: { $containsi: searchTerm } },
                        { document: { $containsi: normalizedSearchTerm || searchTerm } },
                    ],
                },
                sort: ['name:asc'],
                pagination: { page: 1, pageSize: 150 },
                populate: {
                    company: {
                        fields: ['city'],
                    },
                },
                encodeValuesOnly: true,
            });

            const [companiesResponse, subscribersResponse] = await Promise.all([
                api.get('/companies?' + companyQuery),
                api.get('/subscribers?' + subscriberQuery),
            ]);

            const companyResults = (companiesResponse.data.data || []).map((item) => ({
                id: `company-${item.id}`,
                source: 'Empresa',
                name: item.attributes?.social_reason || item.attributes?.trade || '-',
                secondaryName: item.attributes?.trade || '-',
                document: formatCpfCnpj(item.attributes?.document || ''),
                raw: item.attributes || {},
            }));

            const subscribersRaw = subscribersResponse.data.data || [];

            const subscribersDistinctByName = Array.from(
                subscribersRaw.reduce((acc, item) => {
                    const currentName = String(item?.attributes?.name || '').trim();
                    if (!currentName) return acc;

                    const key = currentName.toLowerCase();
                    const existing = acc.get(key);

                    if (!existing) {
                        acc.set(key, item);
                        return acc;
                    }

                    const currentCreatedAt = new Date(item?.attributes?.createdAt || 0).getTime();
                    const existingCreatedAt = new Date(existing?.attributes?.createdAt || 0).getTime();
                    const currentId = Number(item?.id || 0);
                    const existingId = Number(existing?.id || 0);

                    if (currentCreatedAt > existingCreatedAt || (currentCreatedAt === existingCreatedAt && currentId > existingId)) {
                        acc.set(key, item);
                    }

                    return acc;
                }, new Map()).values()
            );

            const subscriberResults = subscribersDistinctByName.map((item) => ({
                id: `subscriber-${item.id}`,
                source: 'Inscrito',
                name: item.attributes?.name || '-',
                secondaryName: '-',
                document: formatCpfCnpj(item.attributes?.document || ''),
                raw: item.attributes || {},
            }));

            setEmitterSearchResults([...companyResults, ...subscriberResults]);
        } catch (err) {
            setAlert({ open: true, type: 'error', msg: 'Erro ao pesquisar emissor por nome/documento.' });
            setEmitterSearchResults([]);
        } finally {
            setEmitterSearchLoading(false);
        }
    };

    const handleSelectEmitterFromModal = (emitter) => {
        const isSubscriber = emitter?.source === 'Inscrito';
        const emitterPhone = isSubscriber
            ? getSubscriberPhone(emitter?.raw || {})
            : emitter?.raw?.phone || '';
        const emitterCity = isSubscriber
            ? getSubscriberCity(emitter?.raw || {})
            : emitter?.raw?.city || '';

        setForm(prev => ({
            ...prev,
            emitterDoc: formatCpfCnpj(emitter?.document || prev.emitterDoc),
            emitterName: emitter?.name || '',
            emitterPhone: maskPhoneBr(emitterPhone),
            emitterCity,
        }));
        setEmitterSearchModalOpen(false);
        setAlert({ open: true, type: 'success', msg: 'Dados do emissor preenchidos automaticamente.' });
    };

    const handleSearchEmitterByDocument = async () => {
        const docDigits = digitsOnly(form.emitterDoc);
        if (![11, 14].includes(docDigits.length)) {
            openEmitterSearchModal();
            return;
        }

        await searchEmitterByDocument(form.emitterDoc);
    };

    const handlePayerCNPJChange = (e) => {
        const formatted = formatCpfCnpj(e.target.value);
        setForm(prev => ({ ...prev, payerDoc: formatted }));
    };

    const openPayerSearchModal = () => {
        setPayerSearchModalOpen(true);
        setPayerSearchAttempted(false);
        setPayerSearchResults([]);
        setPayerSearchName('');
    };

    const handleSearchPayerByName = async () => {
        const searchTerm = payerSearchName.trim();
        const normalizedSearchTerm = digitsOnly(searchTerm);

        if (searchTerm.length < 2) {
            setAlert({ open: true, type: 'warning', msg: 'Digite ao menos 2 caracteres para pesquisar pagador.' });
            return;
        }

        setPayerSearchLoading(true);
        setPayerSearchAttempted(true);
        try {
            const companyQuery = qs.stringify({
                filters: {
                    $or: [
                        { social_reason: { $containsi: searchTerm } },
                        { trade: { $containsi: searchTerm } },
                        { document: { $containsi: normalizedSearchTerm || searchTerm } },
                    ],
                },
                sort: ['social_reason:asc'],
                pagination: { page: 1, pageSize: 150 },
                encodeValuesOnly: true,
            });

            const subscriberQuery = qs.stringify({
                filters: {
                    $or: [
                        { name: { $containsi: searchTerm } },
                        { document: { $containsi: normalizedSearchTerm || searchTerm } },
                    ],
                },
                sort: ['name:asc'],
                pagination: { page: 1, pageSize: 150 },
                populate: {
                    company: {
                        fields: ['city'],
                    },
                },
                encodeValuesOnly: true,
            });

            const [companiesResponse, subscribersResponse] = await Promise.all([
                api.get('/companies?' + companyQuery),
                api.get('/subscribers?' + subscriberQuery),
            ]);

            const companyResults = (companiesResponse.data.data || []).map((item) => ({
                id: `company-${item.id}`,
                source: 'Empresa',
                name: item.attributes?.social_reason || item.attributes?.trade || '-',
                secondaryName: item.attributes?.trade || '-',
                document: formatCpfCnpj(item.attributes?.document || ''),
                raw: item.attributes || {},
            }));

            const subscribersRaw = subscribersResponse.data.data || [];

            // Mantem apenas o ultimo registro por nome (subscriber duplicado).
            const subscribersDistinctByName = Array.from(
                subscribersRaw.reduce((acc, item) => {
                    const currentName = String(item?.attributes?.name || '').trim();
                    if (!currentName) return acc;

                    const key = currentName.toLowerCase();
                    const existing = acc.get(key);

                    if (!existing) {
                        acc.set(key, item);
                        return acc;
                    }

                    const currentCreatedAt = new Date(item?.attributes?.createdAt || 0).getTime();
                    const existingCreatedAt = new Date(existing?.attributes?.createdAt || 0).getTime();
                    const currentId = Number(item?.id || 0);
                    const existingId = Number(existing?.id || 0);

                    if (currentCreatedAt > existingCreatedAt || (currentCreatedAt === existingCreatedAt && currentId > existingId)) {
                        acc.set(key, item);
                    }

                    return acc;
                }, new Map()).values()
            );

            const subscriberResults = subscribersDistinctByName.map((item) => ({
                id: `subscriber-${item.id}`,
                source: 'Inscrito',
                name: item.attributes?.name || '-',
                secondaryName: '-',
                document: formatCpfCnpj(item.attributes?.document || ''),
                raw: item.attributes || {},
            }));

            setPayerSearchResults([...companyResults, ...subscriberResults]);
        } catch (err) {
            setAlert({ open: true, type: 'error', msg: 'Erro ao pesquisar pagador por nome/documento.' });
            setPayerSearchResults([]);
        } finally {
            setPayerSearchLoading(false);
        }
    };

    const handleSelectPayerFromModal = (payer) => {
        setForm(prev => ({
            ...prev,
            payerName: payer?.name || '',
            payerDoc: formatCpfCnpj(payer?.document || prev.payerDoc),
        }));
        setPayerSearchModalOpen(false);
        setAlert({ open: true, type: 'success', msg: 'Dados do pagador preenchidos automaticamente.' });
    };

    const handleSearchPayerByCNPJ = async () => {
        if (lockCriticalFields) return;

        const docDigits = digitsOnly(form.payerDoc);
        if (![11, 14].includes(docDigits.length)) {
            openPayerSearchModal();
            return;
        }

        setSearchingPayer(true);
        try {
            const payer = await getPayerByDocument(form.payerDoc);
            if (payer) {
                setForm(prev => ({
                    ...prev,
                    payerName: payer.name || '',
                    payerDoc: formatCpfCnpj(payer.document || prev.payerDoc),
                }));
                setAlert({ open: true, type: 'success', msg: 'Dados do pagador preenchidos automaticamente.' });
            } else {
                setAlert({ open: true, type: 'warning', msg: 'Pagador não encontrado para o documento informado.' });
            }
        } catch (err) {
            setAlert({ open: true, type: 'error', msg: 'Erro ao buscar pagador pelo documento.' });
        } finally {
            setSearchingPayer(false);
        }
    };

    // ─── Carregar lista de recibos ─── //
    const loadReceipts = useCallback(async (page = 1, search = '') => {
        setLoadingList(true);
        try {
            const filters = {};
            if (search.trim()) {
                filters.$or = [
                    { payer_name: { $containsi: search } },
                    { payer_doc: { $containsi: search } },
                    { emitter_name: { $containsi: search } },
                    { emitter_doc: { $containsi: search } },
                    { receipt_number: { $containsi: search } },
                ];
            }
            const query = qs.stringify({
                filters,
                sort: ['createdAt:desc'],
                pagination: { pageSize: 10, page },
                encodeValuesOnly: true,
            });
            const response = await api.get('/receipts?' + query);
            setReceipts(response.data.data || []);
            setPages(response.data.meta.pagination);
        } catch (err) {
            setAlert({ open: true, type: 'error', msg: 'Erro ao carregar recibos.' });
        } finally {
            setLoadingList(false);
        }
    }, []);

    useEffect(() => { loadReceipts(); }, [loadReceipts]);

    // ─── Salvar recibo ───
    const handleSave = async () => {
        const formToSave = {
            ...form,
            payerName: (form.payerName || '').trim(),
            referente: (form.referente || '').trim(),
            emitterName: (form.emitterName || '').trim(),
        };

        if (!formToSave.payerName && [11, 14].includes(digitsOnly(formToSave.payerDoc).length)) {
            try {
                const payer = await getPayerByDocument(formToSave.payerDoc);
                if (payer) {
                    formToSave.payerName = payer.name || '';
                    setForm(prev => ({
                        ...prev,
                        payerName: formToSave.payerName || prev.payerName,
                        payerDoc: formatCpfCnpj(payer.document || prev.payerDoc),
                    }));
                }
            } catch {
                // silencioso: validação final informa se ainda faltou campo
            }
        }

        if (!formToSave.emitterName && formToSave.emitterDoc.replace(/\D/g, '').length === 14) {
            try {
                const company = await getCompanyByDocument(formToSave.emitterDoc);
                if (company) {
                    formToSave.emitterName = company.social_reason || company.trade || '';
                    setForm(prev => ({
                        ...prev,
                        emitterName: formToSave.emitterName || prev.emitterName,
                        emitterPhone: maskPhoneBr(company.phone || prev.emitterPhone),
                        emitterCity: company.city || prev.emitterCity,
                    }));
                }
            } catch {
                // silencioso: validação final informa se ainda faltou campo
            }
        }

        const missingFields = [];
        if (!formToSave.payerName) missingFields.push('Pagador');
        if (!String(formToSave.value || '').trim()) missingFields.push('Valor');
        if (!formToSave.referente) missingFields.push('Referente');
        if (!formToSave.emitterName) missingFields.push('Emissor');

        if (missingFields.length > 0) {
            setAlert({ open: true, type: 'warning', msg: `Preencha os campos obrigatórios: ${missingFields.join(', ')}.` });
            return;
        }
        setSaving(true);
        try {
            const isEditing = Boolean(receiptNumber.current);
            const payload = {
                payer_name: formToSave.payerName,
                payer_doc: formToSave.payerDoc || null,
                referente: formToSave.referente,
                value: parseCurrencyNumber(formToSave.value) || 0,
                emitter_doc: formToSave.emitterDoc || null,
                emitter_name: formToSave.emitterName,
                emitter_phone: formToSave.emitterPhone || null,
                emitter_city: formToSave.emitterCity || null,
                payment_date: formToSave.paymentDate,
                observations: formToSave.observations || null,
            };

            if (isEditing) {
                const currentId = String(receiptNumber.current);
                const updatedReceipt = await api.put(`/receipts/${currentId}`, { data: payload });
                const updatedId = updatedReceipt?.data?.data?.id || currentId;
                const updatedCreatedAt = updatedReceipt?.data?.data?.attributes?.createdAt;
                receiptNumber.current = String(updatedId);
                if (updatedCreatedAt) {
                    receiptCreatedAt.current = updatedCreatedAt;
                }
                setAlert({ open: true, type: 'success', msg: `Recibo Nº ${updatedId} atualizado com sucesso!` });
            } else {
                const internalReceiptNumber = `${Date.now()}${Math.floor(Math.random() * 1000).toString().padStart(3, '0')}`;
                const createdReceipt = await api.post('/receipts', { data: { ...payload, receipt_number: internalReceiptNumber } });
                const createdId = createdReceipt?.data?.data?.id;
                const createdAt = createdReceipt?.data?.data?.attributes?.createdAt;
                receiptNumber.current = createdId ? String(createdId) : '';
                receiptCreatedAt.current = createdAt || '';
                setAlert({
                    open: true,
                    type: 'success',
                    msg: createdId ? `Recibo Nº ${createdId} salvo com sucesso!` : 'Recibo salvo com sucesso!'
                });
            }

            await loadReceipts(currentPage.current, searchQuery);
            handleClear();
        } catch (err) {
            setAlert({ open: true, type: 'error', msg: 'Erro ao salvar recibo.' });
        } finally {
            setSaving(false);
        }
    };

    // ─── Carregar recibo salvo no formulário ───
    const handleLoad = (row, { autoPrint = false } = {}) => {
        const a = row.attributes;
        receiptNumber.current = String(row.id || '');
        receiptCreatedAt.current = a.createdAt || '';
        setForm({
            payerName: a.payer_name || '',
            payerDoc: a.payer_doc || '',
            referente: a.referente || '',
            value: a.value !== null && a.value !== undefined ? formatCurrency(a.value) : '',
            emitterDoc: a.emitter_doc || '',
            emitterName: a.emitter_name || '',
            emitterPhone: maskPhoneBr(a.emitter_phone || ''),
            emitterCity: a.emitter_city || '',
            paymentDate: a.payment_date || format(new Date(), 'yyyy-MM-dd'),
            observations: a.observations || '',
        });
        setLockCriticalFields(true);
        window.scrollTo({ top: 0, behavior: 'smooth' });

        if (autoPrint) {
            setTimeout(() => handlePrint(), 250);
        }
    };

    // ─── Excluir recibo ───
    const handleDelete = async (id) => {
        try {
            const isCurrentReceiptOpen = String(receiptNumber.current || '') === String(id);
            await api.delete(`/receipts/${id}`);
            setAlert({ open: true, type: 'success', msg: 'Recibo excluído.' });
            if (isCurrentReceiptOpen) {
                handleClear();
            }
            await loadReceipts(currentPage.current, searchQuery);
        } catch {
            setAlert({ open: true, type: 'error', msg: 'Erro ao excluir recibo.' });
        } finally {
            setDeleteConfirm({ open: false, id: null });
        }
    };

    const buildReceiptPdf = async () => {
        if (!receiptRef.current) {
            throw new Error('Preview do recibo nao encontrado.');
        }

        const canvas = await html2canvas(receiptRef.current, {
            scale: 2,
            useCORS: true,
            backgroundColor: '#ffffff',
            logging: false,
        });

        const pngDataUrl = canvas.toDataURL('image/png');
        const pngBytes = await fetch(pngDataUrl).then((res) => res.arrayBuffer());

        const pdfDoc = await PDFDocument.create();
        const page = pdfDoc.addPage([595.28, 841.89]);
        const image = await pdfDoc.embedPng(pngBytes);

        const { width, height } = page.getSize();
        const maxWidth = width - 30;
        const maxHeight = height - 30;
        const imageRatio = image.width / image.height;

        let drawWidth = maxWidth;
        let drawHeight = drawWidth / imageRatio;

        if (drawHeight > maxHeight) {
            drawHeight = maxHeight;
            drawWidth = drawHeight * imageRatio;
        }

        const x = (width - drawWidth) / 2;
        const y = (height - drawHeight) / 2;

        page.drawImage(image, {
            x,
            y,
            width: drawWidth,
            height: drawHeight,
        });

        const pdfBytes = await pdfDoc.save();
        const blob = new Blob([pdfBytes], { type: 'application/pdf' });
        return URL.createObjectURL(blob);
    };

    const handlePrint = async () => {
        try {
            setGeneratingPdf(true);
            if (pdfPreviewUrl) {
                URL.revokeObjectURL(pdfPreviewUrl);
            }
            const url = await buildReceiptPdf();
            setPdfPreviewUrl(url);
            setPdfPreviewOpen(true);
        } catch {
            setAlert({ open: true, type: 'error', msg: 'Erro ao gerar PDF para visualizacao.' });
        } finally {
            setGeneratingPdf(false);
        }
    };

    const handleClosePdfPreview = () => {
        setPdfPreviewOpen(false);
        if (pdfPreviewUrl) {
            URL.revokeObjectURL(pdfPreviewUrl);
            setPdfPreviewUrl('');
        }
    };

    const handleClear = () => {
        receiptNumber.current = '';
        receiptCreatedAt.current = '';
        setForm(createEmptyForm(defaultEmitterDoc.current));
        setLockCriticalFields(false);
        if (defaultEmitterDoc.current) {
            searchEmitterByDocument(defaultEmitterDoc.current, { silent: true });
        }
    };

    return (
        <Box sx={{ width: '100%', p: { xs: 2, md: 4 } }}>
            <Snackbar
                open={alert.open}
                autoHideDuration={3500}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                onClose={() => setAlert(a => ({ ...a, open: false }))}
            >
                <Alert severity={alert.type} sx={{ width: '100%' }}>{alert.msg}</Alert>
            </Snackbar>

            {/* Confirmar exclusão */}
            <Dialog open={deleteConfirm.open} onClose={() => setDeleteConfirm({ open: false, id: null })}>
                <DialogTitle>Confirmar exclusão</DialogTitle>
                <DialogContent>
                    <Typography>Tem certeza que deseja excluir este recibo? Esta ação não pode ser desfeita.</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteConfirm({ open: false, id: null })}>Cancelar</Button>
                    <Button color="error" variant="contained" onClick={() => handleDelete(deleteConfirm.id)}>Excluir</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={payerSearchModalOpen} onClose={() => setPayerSearchModalOpen(false)} fullWidth maxWidth="md">
                <DialogTitle>Pesquisar Pagador por Nome</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary" mb={2}>
                        Pesquise por nome ou documento (CPF/CNPJ) para preencher os dados do pagador.
                    </Typography>
                    <Box display="flex" gap={1.5} mb={2}>
                        <TextField
                            fullWidth
                            label="Nome ou CPF/CNPJ"
                            value={payerSearchName}
                            onChange={(e) => setPayerSearchName(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    e.preventDefault();
                                    handleSearchPayerByName();
                                }
                            }}
                        />
                        <Button
                            variant="contained"
                            onClick={handleSearchPayerByName}
                            startIcon={payerSearchLoading ? <CircularProgress size={16} /> : <SearchIcon />}
                            disabled={payerSearchLoading}
                        >
                            Buscar
                        </Button>
                    </Box>

                    {payerSearchLoading ? (
                        <Box display="flex" justifyContent="center" py={3}>
                            <CircularProgress size={26} />
                        </Box>
                    ) : payerSearchResults.length > 0 ? (
                        <TableContainer component={Paper} variant="outlined">
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Origem</TableCell>
                                        <TableCell>Razão Social</TableCell>
                                        <TableCell>Nome Fantasia</TableCell>
                                        <TableCell>CPF/CNPJ</TableCell>
                                        <TableCell align="right">Ação</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {payerSearchResults.map((payer) => (
                                        <TableRow key={payer.id} hover>
                                            <TableCell>{payer.source}</TableCell>
                                            <TableCell>{payer.name || '-'}</TableCell>
                                            <TableCell>{payer.secondaryName || '-'}</TableCell>
                                            <TableCell>{payer.document || '-'}</TableCell>
                                            <TableCell align="right">
                                                <Button size="small" onClick={() => handleSelectPayerFromModal(payer)}>
                                                    Selecionar
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    ) : payerSearchAttempted ? (
                        <Typography variant="body2" color="text.secondary">
                            Nenhuma empresa encontrada para o nome informado.
                        </Typography>
                    ) : null}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setPayerSearchModalOpen(false)}>Fechar</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={emitterSearchModalOpen} onClose={() => setEmitterSearchModalOpen(false)} fullWidth maxWidth="md">
                <DialogTitle>Pesquisar Emissor por Nome</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary" mb={2}>
                        Pesquise por razão social, nome fantasia ou CNPJ para preencher os dados do emissor.
                    </Typography>
                    <Box display="flex" gap={1.5} mb={2}>
                        <TextField
                            fullWidth
                            label="Razão social, fantasia ou CNPJ"
                            value={emitterSearchName}
                            onChange={(e) => setEmitterSearchName(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    e.preventDefault();
                                    handleSearchEmitterByName();
                                }
                            }}
                        />
                        <Button
                            variant="contained"
                            onClick={handleSearchEmitterByName}
                            startIcon={emitterSearchLoading ? <CircularProgress size={16} /> : <SearchIcon />}
                            disabled={emitterSearchLoading}
                        >
                            Buscar
                        </Button>
                    </Box>

                    {emitterSearchLoading ? (
                        <Box display="flex" justifyContent="center" py={3}>
                            <CircularProgress size={26} />
                        </Box>
                    ) : emitterSearchResults.length > 0 ? (
                        <TableContainer component={Paper} variant="outlined">
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Origem</TableCell>
                                        <TableCell>Razão Social / Nome</TableCell>
                                        <TableCell>Nome Fantasia</TableCell>
                                        <TableCell>CPF/CNPJ</TableCell>
                                        <TableCell align="right">Ação</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {emitterSearchResults.map((emitter) => (
                                        <TableRow key={emitter.id} hover>
                                            <TableCell>{emitter.source}</TableCell>
                                            <TableCell>{emitter.name || '-'}</TableCell>
                                            <TableCell>{emitter.secondaryName || '-'}</TableCell>
                                            <TableCell>{emitter.document || '-'}</TableCell>
                                            <TableCell align="right">
                                                <Button size="small" onClick={() => handleSelectEmitterFromModal(emitter)}>
                                                    Selecionar
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    ) : emitterSearchAttempted ? (
                        <Typography variant="body2" color="text.secondary">
                            Nenhum emissor encontrado para o termo informado.
                        </Typography>
                    ) : null}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setEmitterSearchModalOpen(false)}>Fechar</Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={pdfPreviewOpen}
                onClose={handleClosePdfPreview}
                fullWidth
                maxWidth="lg"
            >
                <DialogTitle>Pre-visualizacao do PDF</DialogTitle>
                <DialogContent dividers sx={{ p: 0, minHeight: '72vh' }}>
                    {generatingPdf ? (
                        <Box display="flex" alignItems="center" justifyContent="center" height="72vh">
                            <CircularProgress size={30} />
                        </Box>
                    ) : (
                        <iframe
                            title="PDF do recibo"
                            src={pdfPreviewUrl ? `${pdfPreviewUrl}#toolbar=1&navpanes=0&scrollbar=1` : undefined}
                            style={{ width: '100%', height: '72vh', border: 'none' }}
                        />
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClosePdfPreview}>Fechar</Button>
                </DialogActions>
            </Dialog>

            <Typography variant="h5" fontWeight={700} mb={3}>Gerar Recibo</Typography>

            <Grid container spacing={4}>
                {/* ─── Formulário ─── */}
                <Grid item xs={12} md={5}>
                    <Paper elevation={3} sx={{ p: 3, borderRadius: 2 }}>
                        <Typography variant="subtitle1" fontWeight={600} mb={2}>Emissor</Typography>

                        <Grid container spacing={2}>
                            <Grid item xs={9}>
                                <TextField
                                    fullWidth
                                    label="CPF/CNPJ do Emissor"
                                    value={form.emitterDoc}
                                    onChange={handleEmitterCNPJChange}
                                    placeholder="000.000.000-00 ou 00.000.000/0001-00"
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            e.preventDefault();
                                            handleSearchEmitterByDocument();
                                        }
                                    }}
                                />
                            </Grid>
                            <Grid item xs={3}>
                                <Button
                                    fullWidth
                                    variant="outlined"
                                    color="primary"
                                    startIcon={searchingEmitter ? <CircularProgress size={16} /> : <SearchIcon />}
                                    onClick={handleSearchEmitterByDocument}
                                    disabled={searchingEmitter}
                                    sx={{ height: '100%' }}
                                >
                                    Buscar
                                </Button>
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Nome / Razão Social do Emissor"
                                    value={form.emitterName}
                                    onChange={handleField('emitterName')}
                                    helperText="Use CPF/CNPJ e o botão Buscar para pesquisar emitentes já cadastrados."
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <TextField
                                    fullWidth
                                    label="Telefone (opcional)"
                                    value={form.emitterPhone}
                                    onChange={handleEmitterPhoneChange}
                                    placeholder="(00) 00000-0000"
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <TextField fullWidth label="Cidade" value={form.emitterCity} onChange={handleField('emitterCity')} />
                            </Grid>
                        </Grid>

                        <Divider sx={{ my: 3 }} />
                        <Typography variant="subtitle1" fontWeight={600} mb={2}>Pagamento</Typography>

                        <Grid container spacing={2}>
                            <Grid item xs={9}>
                                <TextField
                                    fullWidth
                                    label="CPF/CNPJ do Pagador"
                                    value={form.payerDoc}
                                    onChange={handlePayerCNPJChange}
                                    disabled={lockCriticalFields}
                                    placeholder="000.000.000-00 ou 00.000.000/0001-00"
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            e.preventDefault();
                                            handleSearchPayerByCNPJ();
                                        }
                                    }}
                                />
                            </Grid>
                            <Grid item xs={3}>
                                <Button
                                    fullWidth
                                    variant="outlined"
                                    color="primary"
                                    startIcon={searchingPayer ? <CircularProgress size={16} /> : <SearchIcon />}
                                    onClick={handleSearchPayerByCNPJ}
                                    disabled={searchingPayer || lockCriticalFields}
                                    sx={{ height: '100%' }}
                                >
                                    Buscar
                                </Button>
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Pagador"
                                    value={form.payerName}
                                    onChange={handleField('payerName')}
                                    disabled={lockCriticalFields}
                                    helperText={
                                        lockCriticalFields
                                            ? 'Campo bloqueado após geração do recibo.'
                                            : 'Use CPF/CNPJ e a lupa para buscar em empresas e inscritos.'
                                    }
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Valor (R$)"
                                    value={form.value}
                                    onChange={handleValueChange}
                                    disabled={lockCriticalFields}
                                    placeholder="0,00"
                                    InputProps={{ startAdornment: <InputAdornment position="start">R$</InputAdornment> }}
                                    helperText={lockCriticalFields ? 'Campo bloqueado após geração do recibo.' : ''}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    multiline
                                    minRows={2}
                                    label="Referente a"
                                    value={form.referente}
                                    onChange={handleField('referente')}
                                    placeholder="Ex: prestação de serviços de consultoria referente ao mês de janeiro/2026"
                                />
                            </Grid>
                        
                            <Grid item xs={6}>
                                <TextField fullWidth type="date" label="Data do Pagamento" value={form.paymentDate} onChange={handleField('paymentDate')} InputLabelProps={{ shrink: true }} />
                            </Grid>
                        </Grid>

                        <Divider sx={{ my: 3 }} />
                        <Typography variant="subtitle1" fontWeight={600} mb={2}>Observações</Typography>
                        <TextField
                            fullWidth
                            multiline
                            minRows={3}
                            label="Texto livre / Observações"
                            value={form.observations}
                            onChange={handleField('observations')}
                            placeholder="Informe aqui observações adicionais que deseja incluir no recibo..."
                        />

                        <Box display="flex" gap={2} mt={3} flexWrap="wrap">
                            <Button
                                variant="contained"
                                color="success"
                                startIcon={saving ? <CircularProgress size={18} /> : <SaveIcon />}
                                onClick={handleSave}
                                disabled={saving}
                                fullWidth
                            >
                                {saving ? 'Salvando...' : 'Salvar Recibo'}
                            </Button>
                            <Button variant="contained" startIcon={<PrintIcon />} onClick={handlePrint} fullWidth>
                                Imprimir / PDF
                            </Button>
                            <Button variant="outlined" color="inherit" startIcon={<AddIcon />} onClick={handleClear} fullWidth>
                                Novo Recibo
                            </Button>
                        </Box>
                    </Paper>
                </Grid>

                {/* ─── Preview do Recibo ─── */}
                <Grid item xs={12} md={7}>
                    <Box ref={receiptRef}>
                        <ReciboPrint
                            form={form}
                            receiptNumber={receiptNumber.current}
                            receiptCreatedAt={receiptCreatedAt.current}
                            logoUrl={configLogoUrl}
                            basePath={basePath}
                            apeprevDoc={defaultEmitterDoc.current}
                        />
                    </Box>
                </Grid>
            </Grid>

            {/* ─── Lista de Recibos Salvos ─── */}
            <Box mt={6}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={2}>
                    <Typography variant="h6" fontWeight={700}>Recibos Salvos</Typography>
                    <TextField
                        size="small"
                        label="Buscar"
                        placeholder="Pagador, emissor, CNPJ ou nº recibo..."
                        value={searchQuery}
                        onChange={(e) => {
                            setSearchQuery(e.target.value);
                            currentPage.current = 1;
                            loadReceipts(1, e.target.value);
                        }}
                        InputProps={{
                            endAdornment: <InputAdornment position="end"><SearchIcon fontSize="small" color="action" /></InputAdornment>
                        }}
                        sx={{ width: 320 }}
                    />
                </Box>

                <TableContainer component={Paper} elevation={2}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell><strong>Nº</strong></TableCell>
                                <TableCell><strong>Data</strong></TableCell>
                                <TableCell><strong>Pagador</strong></TableCell>
                                <TableCell><strong>Emissor</strong></TableCell>
                                <TableCell><strong>Referente</strong></TableCell>
                                <TableCell align="right"><strong>Valor</strong></TableCell>
                                <TableCell align="center"><strong>Ações</strong></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loadingList ? (
                                <TableRow>
                                    <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                                        <CircularProgress size={28} />
                                    </TableCell>
                                </TableRow>
                            ) : receipts.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={8} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                                        Nenhum recibo encontrado
                                    </TableCell>
                                </TableRow>
                            ) : receipts.map((row) => {
                                const a = row.attributes;
                                return (
                                    <TableRow key={row.id} hover>
                                        <TableCell>{row.id}</TableCell>
                                        <TableCell>{a.createdAt ? format(new Date(a.createdAt), 'dd/MM/yyyy') : '-'}</TableCell>
                                        <TableCell>{a.payer_name}</TableCell>
                                        <TableCell>
                                            <Typography variant="body2" noWrap maxWidth={180}>{a.emitter_name}</Typography>
                                            {a.emitter_doc && <Typography variant="caption" color="text.secondary">{a.emitter_doc}</Typography>}
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" noWrap maxWidth={200}>{a.referente}</Typography>
                                        </TableCell>
                                        <TableCell align="right">
                                            <strong>R$ {formatCurrency(a.value ?? 0)}</strong>
                                        </TableCell>                                        <TableCell align="center">
                                            <Stack direction="row" spacing={0} justifyContent="center">
                                                <Tooltip title="Imprimir">
                                                    <IconButton color="primary" size="small" onClick={() => handleLoad(row, { autoPrint: true })}>
                                                        <PrintIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Editar recibo">
                                                    <IconButton color="info" size="small" onClick={() => handleLoad(row)}>
                                                        <EditIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Excluir">
                                                    <IconButton color="error" size="small" onClick={() => setDeleteConfirm({ open: true, id: row.id })}>
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            </Stack>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>

                <Stack direction="row" justifyContent="flex-end" mt={2}>
                    <Pagination
                        count={pages.pageCount || 1}
                        page={currentPage.current}
                        color="primary"
                        onChange={(_, value) => {
                            currentPage.current = value;
                            loadReceipts(value, searchQuery);
                        }}
                    />
                </Stack>
            </Box>
        </Box>
    );
}

function ReciboPrint({ form, receiptNumber, receiptCreatedAt, logoUrl, basePath, apeprevDoc }) {
    const formattedDate = receiptCreatedAt
        ? format(new Date(receiptCreatedAt), 'dd/MM/yyyy')
        : '__/__/____';

    const valorFormatado = form.value ? `R$ ${formatCurrency(form.value)}` : 'R$ _______________';
    const valorPorExtenso = form.value ? currencyToWords(form.value) : '';
    const isApeprevEmitter =
        basePath === 'apeprev'
        && digitsOnly(form.emitterDoc) !== ''
        && digitsOnly(form.emitterDoc) === digitsOnly(apeprevDoc);

    return (
        <Paper
            className="receipt-wrap"
            elevation={4}
            sx={{
                p: 4,
                borderRadius: 2,
                fontFamily: 'Arial, sans-serif',
                fontSize: '14px',
                width: '100%',
                maxWidth: '190mm',
                minHeight: '130mm',
                mx: 'auto',
            }}
        >
            {/* Cabeçalho */}
            <Box className="receipt-header" sx={{ textAlign: 'center', borderBottom: '2px solid #333', pb: 1.5, mb: 2 }}>
                <Box className="receipt-header-top" sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                    {logoUrl && (
                        <Box
                            component="img"
                            src={logoUrl}
                            alt="Logo da empresa"
                            className="receipt-logo"
                            sx={{ maxWidth: 180, maxHeight: 70, objectFit: 'contain' }}
                        />
                    )}
                    <Box className="receipt-header-text">
                        <Typography variant="h5" fontWeight={800} letterSpacing={4} textTransform="uppercase">
                            Recibo
                        </Typography>
                        <Typography variant="caption" color="text.secondary">Nº {receiptNumber}</Typography>
                    </Box>
                </Box>
            </Box>

            {/* Valor em destaque */}
            <Box className="clearfix" sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                <Box className="receipt-value-box" sx={{ border: '2px solid #333', px: 2.5, py: 1, fontWeight: 700, fontSize: '1.3rem' }}>
                    {valorFormatado}
                </Box>
            </Box>

            {/* Corpo */}
            <Box className="receipt-body" sx={{ lineHeight: 2.2 }}>
                <Typography variant="body1">
                    Recebi de{' '}
                    <Box component="span" sx={{ borderBottom: '1px solid #555', display: 'inline-block', minWidth: 220, px: 0.5, fontWeight: 600 }}>
                        {form.payerName || ''}
                    </Box>
                    {form.payerDoc && (
                        <>
                            {' '}portador do CPF/CNPJ{' '}
                            <Box component="span" sx={{ borderBottom: '1px solid #555', display: 'inline-block', minWidth: 140, px: 0.5 }}>
                                {form.payerDoc}
                            </Box>
                        </>
                    )}
                    {', '}a importância de{' '}
                    <Box component="span" sx={{display: 'inline-block', px: 1, fontWeight: 700 }}>
                        {valorFormatado}  {valorPorExtenso && `(${valorPorExtenso})`}
                    </Box>
                </Typography>

                

                <Typography variant="body1" mt={1}>
                    referente a{' '}
                    <Box component="span" sx={{ borderBottom: '1px solid #555', display: 'inline-block', minWidth: 300, px: 0.5 }}>
                        {form.referente || ''}
                    </Box>
                </Typography>


                {form.observations && (
                    <Box className="obs-block" sx={{ mt: 2, p: 1.5, backgroundColor: '#f9f9f9', borderRadius: 1, fontSize: '13px', color: '#444' }}>
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{form.observations}</Typography>
                    </Box>
                )}
            </Box>

            <Divider sx={{ borderStyle: 'dashed', my: 3 }} />

            {/* Emissor e data */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap', gap: 2 }}>
                <Box>
                    <Typography variant="body2">
                        Emissor:{' '}
                        <Box component="span" sx={{ fontWeight: 600 }}>{form.emitterName || '___________________________'}</Box>
                    </Typography>
                    {form.emitterDoc && (
                        <Typography variant="body2">CNPJ: {form.emitterDoc}</Typography>
                    )}
                    {form.emitterPhone && (
                        <Typography variant="body2">Tel.: {form.emitterPhone}</Typography>
                    )}
                </Box>
                <Box textAlign="right">
                    <Typography variant="body2">
                        {form.emitterCity || '_________________'}, {formattedDate}
                    </Typography>
                </Box>
            </Box>

            {/* Assinaturas */}
            <Box className="receipt-footer" sx={{ mt: 5 }}>
                <Box className="signature-row" sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                    <Box className="signature-item" sx={{ flex: 1, minWidth: 220, alignContent: 'center', textAlign: 'center' }}>
                        <Typography className="signature-label" variant="caption" display="block">
                           {isApeprevEmitter ? (
                        <>
                            <img src={Assinatura} alt="Assinatura" width={140} style={{ "transform": "rotate(22deg)", "margin-top": '-20px' }} />
                            <Typography variant="body2" sx={{ marginTop: -4, textDecoration: "overline" }}>
                                Presidente da Apeprev
                            </Typography>
                        </>
                    ) : (
                        <Box sx={{ height: 56 }} />
                    )}
                        </Typography>
                        {!isApeprevEmitter && (
                            <>
                                <Typography variant="body2" sx={{ textDecoration: 'overline', mt: 1 }}>
                                    {form.emitterName || '____________________________'}
                                </Typography>
                                {form.emitterDoc && (
                                    <Typography variant="caption" display="block">{form.emitterDoc}</Typography>
                                )}
                            </>
                        )}
                    </Box>
                </Box>
            </Box>
        </Paper>
    );
}
